<script lang="ts">
	//Helpers
	import {
		get_gen_1_path_from_category,
		get_local_ip,
		get_mode,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	//Services
	import { localReq, postReq } from '../../../../../services/http';
	import { format } from '../../../../../services/i18n';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Button, Radio, toast, Tooltip } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Utilities
	import { tooltipText } from '../tooltip_text';

	export let device;

	let types = ['on', 'off', 'last', 'switch'];

	const defaultOptionsLegacy = [
		$format('power_on_default_on', {
			default: 'On: this {category} device will turn on, when it has power.',
		}),
		$format('power_on_default_off', {
			default: 'Off: this {category} device will turn off, when it has power.',
		}),
		$format('power_on_default_last', {
			default: 'Restore last category: this {category} device will use its latest category.',
		}),
		$format('power_on_default_switch', {
			values: { n: '' },
			default:
				'SWITCH - Operates this {category} according to the state of the switch or button connected to the device.',
		}),
	];

	const defaultOptionsNewLayout = [
		{
			title: $format('power_on_default_on_new_layout_title', {
				default: 'Turn on',
			}),
			description: $format('power_on_default_on_new_layout_description', {
				default: 'Automatically turns the device on when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_off_new_layout_title', {
				default: 'Turn off',
			}),
			description: $format('power_on_default_off_new_layout_description', {
				default: 'Keeps the device off when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_last_new_layout_title', {
				default: 'Restore last state',
			}),
			description: $format('power_on_default_last_new_layout_description', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
		{
			title: $format('power_on_default_switch_new_layout_title', {
				default: 'Match input',
			}),
			description: $format('power_on_default_switch_new_layout_description', {
				default:
					'Operates the device according to the state of the switch or button connected to the device.',
			}),
		},
	];

	const id = real_id(device.id);

	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	let category = get_gen_1_path_from_category(device);

	let selected = settings?.[category]?.[device.channel]?.default_state;
	let cache = selected;

	let toolTipMesage = () => {
		return {
			message: 'power_on_default_unavailable_edge_or_momentary_info_toast',
		};
	};

	let loading = false;

	const disabled =
		typeof settings[category][device.channel]?.btn_type !== 'undefined' &&
		(settings[category][device.channel].btn_type === 'edge' ||
			settings[category][device.channel]?.btn_type.includes('momentary') ||
			settings[category][device.channel]?.btn_type === 'one_button' ||
			settings[category][device.channel]?.btn_type === 'dual_button');

	let localIp = get_local_ip(id);

	const setDefault = (state) => {
		selected = state;
	};

	const sentRequest = async () => {
		if (cache !== selected) {
			let res;
			loading = true;
			if (localIp) {
				res = await localReq(
					device,
					`${localIp}/settings/${device.category}/${device.channel}`,
					'post',
					{
						default_state: selected,
					},
				);
			} else {
				res = await postReq(`device/${device.category}/settings/default_state`, {
					channel: device.channel,
					state: selected,
					id: id,
				});
			}
			if (res.isok) {
				settings[category][device.channel].default_state = selected;
				toast.open({
					type: 'success',
					title:
						$format('power_on_default_scc_toast') || `Power on was changed to ${selected}.`,
				});
				devices_settings.update_devices_settings(device.id, settings);
				cache = selected;
			} else {
				toast.open({ type: 'danger', title: $format('error_global') });
				selected = cache;
			}
			loading = false;
		}
	};
</script>

{#if $showNewLayout}
	{#each types as _type, i}
		{#if _type === 'switch'}
			<Tooltip placement="top" title={tooltipText(toolTipMesage(), disabled)}>
				<Radio
					class="mb-s"
					{disabled}
					size="m"
					{selected}
					value={_type}
					on:click={() => setDefault(_type)}
					id={`power-on-${_type}`}
					info={defaultOptionsNewLayout[i].description}
				>
					{defaultOptionsNewLayout[i].title}
				</Radio>
			</Tooltip>
		{:else}
			<Radio
				class="mb-s"
				size="m"
				{selected}
				value={_type}
				on:click={() => setDefault(_type)}
				id={`power-on-${_type}`}
				info={defaultOptionsNewLayout[i].description}
			>
				{defaultOptionsNewLayout[i].title}
			</Radio>
		{/if}
	{/each}
{:else}
	<Radio size="m" {selected} value={types[0]} on:click={() => setDefault(types[0])} id="radio-vrvfhnz"
		>{defaultOptionsLegacy[0]}</Radio
	>
	<Radio size="m" {selected} value={types[1]} on:click={() => setDefault(types[1])} id="radio-gn3bbee"
		>{defaultOptionsLegacy[1]}</Radio
	>
	<Radio size="m" {selected} value={types[2]} on:click={() => setDefault(types[2])} id="radio-n5b5pnv"
		>{defaultOptionsLegacy[2]}</Radio
	>
	<Tooltip placement="top" title={tooltipText(toolTipMesage(), disabled)}>
		<Radio
			{disabled}
			size="m"
			{selected}
			value={types[3]}
			on:click={() => setDefault(types[3])}
			id="radio-g12p1ya">{defaultOptionsLegacy[3]}</Radio
		>
	</Tooltip>
{/if}

<Button class={$showNewLayout ? 'mt-m' : 'mt-s'} {loading} size="m" width="full" on:click={sentRequest}
	>{$format('button_save')}</Button
>
